import { supabase } from "~/integrations/supabase/client";
import type { AssemblyLine, AssemblyLineInput } from "~/types/production";

export const assemblyLineService = {
  /**
   * Get assembly lines (Líneas de Envasado) ordered for display.
   * By default only active lines are returned.
   */
  async getAll(includeInactive = false): Promise<AssemblyLine[]> {
    let query = supabase
      .from("assembly_lines")
      .select("*")
      .order("sort_order", { ascending: true });

    if (!includeInactive) {
      query = query.eq("is_active", true);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as AssemblyLine[];
  },

  /**
   * Create a new assembly line. `code` must be unique.
   */
  async create(input: AssemblyLineInput): Promise<AssemblyLine> {
    const { data, error } = await supabase
      .from("assembly_lines")
      .insert({
        code: input.code,
        name: input.name ?? null,
        plant: input.plant ?? null,
        is_active: input.is_active ?? true,
        sort_order: input.sort_order ?? 0,
      })
      .select("*")
      .single();

    if (error) throw error;
    return data as AssemblyLine;
  },

  /**
   * Update an existing assembly line.
   */
  async update(
    id: string,
    input: Partial<AssemblyLineInput>
  ): Promise<AssemblyLine> {
    const { data, error } = await supabase
      .from("assembly_lines")
      .update({
        ...(input.code !== undefined && { code: input.code }),
        ...(input.name !== undefined && { name: input.name }),
        ...(input.plant !== undefined && { plant: input.plant }),
        ...(input.is_active !== undefined && { is_active: input.is_active }),
        ...(input.sort_order !== undefined && { sort_order: input.sort_order }),
      })
      .eq("id", id)
      .select("*")
      .single();

    if (error) throw error;
    return data as AssemblyLine;
  },

  /**
   * Permanently delete an assembly line. Prefer deactivating (is_active = false)
   * for lines already referenced by historical production batches.
   */
  async remove(id: string): Promise<void> {
    const { error } = await supabase
      .from("assembly_lines")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },
};
